/**
 * Freeform parse-and-enrich lookup extracted verbatim from
 * `ContactEditModal.tsx`. The orchestrator keeps the (non-memoized)
 * `handleFreeformFind` shell and delegates the body here, parameterized on a
 * typed deps object. Behavior is unchanged (#3237 Pass-2).
 *
 * The deps carry the VALUES captured at call time (`freeformText`,
 * `gmailResults`, `doNotBulkEnrichLocal`, `summary`) plus the field setters —
 * preserving the original closure semantics where the post-await
 * blank-fill guards (`!parsed.company`, `!summary`) read the value
 * snapshotted when the handler fired.
 */

import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { emailApiBase } from '@/lib/email-api';

export interface FreeformLookupDeps {
  freeformText: string;
  gmailResults: Array<{ email: string; name: string; photoUrl: string | null; score: number }>;
  doNotBulkEnrichLocal: boolean;
  summary: string;
  setFreeformParsing: (b: boolean) => void;
  setFreeformParsed: (p: Record<string, any> | null) => void;
  setFirstName: (s: string) => void;
  setLastName: (s: string) => void;
  setName: (s: string) => void;
  setEmail: (s: string) => void;
  setPhone: (s: string) => void;
  setCompany: (s: string) => void;
  setFirm: (s: string) => void;
  setContactTitle: (s: string) => void;
  setLocation: (s: string) => void;
  setEnrichHints: (s: string) => void;
  setNewNote: (s: string) => void;
  setEnrichLoading: (b: boolean) => void;
  setEnrichError: (s: string | null) => void;
  setEnrichResult: (r: { confidence: string; fields: Record<string, any>; rawSummary: string } | null) => void;
  setEnrichmentSummary: (s: string) => void;
  setEnrichmentSkills: (s: string[]) => void;
  setEnrichmentConfidence: (s: string) => void;
  setLinkedinUrl: (s: string) => void;
  setXHandle: (s: string) => void;
  setSummary: (s: string) => void;
}

export async function handleFreeformFind(deps: FreeformLookupDeps): Promise<void> {
  const {
    freeformText, gmailResults, doNotBulkEnrichLocal, summary,
    setFreeformParsing, setFreeformParsed, setFirstName, setLastName, setName, setEmail,
    setPhone, setCompany, setFirm, setContactTitle, setLocation, setEnrichHints, setNewNote,
    setEnrichLoading, setEnrichError, setEnrichResult, setEnrichmentSummary, setEnrichmentSkills,
    setEnrichmentConfidence, setLinkedinUrl, setXHandle, setSummary,
  } = deps;

  if (!freeformText.trim()) return;
  setFreeformParsing(true);
  try {
    const { apiPost } = await import('@/lib/api/client');
    // Step 1: Parse the freeform text into structured fields
    const { data: parsed } = await apiPost<Record<string, any>>('/contacts/parse', { text: freeformText });
    // Defense against the parser dumping unrelated tokens into lastName
    // (seen with mixed input like "vincent mirisch vincent.bronek@gmail.com
    // NYU" → lastName became the email + hint). Strip anything email-shaped
    // out of the name fields, push leftover tokens into searchHints so
    // they still inform AI search.
    const stripFromName = (raw: unknown): { clean: string; leftover: string[] } => {
      if (typeof raw !== 'string' || !raw.trim()) return { clean: '', leftover: [] };
      const tokens = raw.trim().split(/\s+/);
      const nameTokens: string[] = [];
      const leftover: string[] = [];
      for (const tok of tokens) {
        if (/@/.test(tok) || /^https?:\/\//i.test(tok)) leftover.push(tok);
        else nameTokens.push(tok);
      }
      return { clean: nameTokens.join(' ').trim(), leftover };
    };
    const firstClean = stripFromName(parsed.firstName);
    const lastClean = stripFromName(parsed.lastName);
    // Last name should be a single surname (or hyphenated); collapse any
    // extra tokens past the first into the hints bucket.
    const lastTokens = lastClean.clean ? lastClean.clean.split(/\s+/) : [];
    const lastNameOnly = lastTokens.shift() || '';
    const overflowFromLast = lastTokens; // becomes hints
    parsed.firstName = firstClean.clean || parsed.firstName || '';
    parsed.lastName = lastNameOnly;
    const harvestedHints = [
      ...firstClean.leftover.filter(t => !/@/.test(t)),
      ...lastClean.leftover.filter(t => !/@/.test(t)),
      ...overflowFromLast,
    ].filter(Boolean);
    if (harvestedHints.length > 0) {
      const existing = (parsed.searchHints || '').toString().trim();
      const merged = [existing, harvestedHints.join(' ')].filter(Boolean).join(', ');
      parsed.searchHints = merged;
    }
    // Promote a stray email token from name fields if the parser missed it.
    if (!parsed.email) {
      const allLeftover = [...firstClean.leftover, ...lastClean.leftover];
      const emailTok = allLeftover.find(t => /@/.test(t));
      if (emailTok) parsed.email = emailTok.replace(/[.,;]+$/, '');
    }
    setFreeformParsed(parsed);

    // If no email parsed, search Gmail for the name to find their email
    if (!parsed.email) {
      const gmailMatch = gmailResults.length > 0 ? gmailResults[0] : null;
      if (gmailMatch?.email) {
        parsed.email = gmailMatch.email;
      } else {
        // Gmail results may have cleared — search by parsed name
        const searchName = `${parsed.firstName || ''} ${parsed.lastName || ''}`.trim();
        if (searchName) {
          try {
            const token = getAuthItem('xavior_auth_token');
            const userId = getAuthItem('userId');
            const hdrs: Record<string, string> = { 'Content-Type': 'application/json', 'X-Workspace-Id': getActiveWorkspaceId() };
            if (token) hdrs.Authorization = `Bearer ${token}`;
            if (userId) hdrs['X-Actor-Id'] = userId;
            const gmailRes = await fetch(`${emailApiBase()}/contacts-search?query=${encodeURIComponent(searchName)}&limit=1`, { headers: hdrs });
            if (gmailRes.ok) {
              const gmailData = await gmailRes.json();
              const people = gmailData.people || [];
              if (people.length > 0 && people[0].email) {
                parsed.email = people[0].email;
              }
            }
          } catch { /* ignore */ }
        }
      }
    }

    // Pre-fill form fields from parsed data
    if (parsed.firstName) setFirstName(parsed.firstName);
    if (parsed.lastName) setLastName(parsed.lastName);
    if (parsed.email) setEmail(parsed.email);
    if (parsed.phone) setPhone(parsed.phone);
    if (parsed.company) { setCompany(parsed.company); setFirm(parsed.company); }
    if (parsed.title) setContactTitle(parsed.title);
    if (parsed.location) setLocation(parsed.location);
    if (parsed.searchHints) setEnrichHints(parsed.searchHints);
    if (parsed.notes) setNewNote(parsed.notes);

    // Step 2: If we got a name and the user hasn't opted out, auto-enrich
    // (skip duplicate check — let user confirm via Save)
    const searchName = `${parsed.firstName || ''} ${parsed.lastName || ''}`.trim();
    if (searchName && !doNotBulkEnrichLocal) {
      setName(searchName);
      // Run enrichment with parsed hints
      setEnrichLoading(true);
      setEnrichError(null);
      const hints = [parsed.company, parsed.searchHints].filter(Boolean).join(', ');
      // Pass known data from Gmail/parsed for better enrichment
      const knownData: Record<string, string> = {};
      if (parsed.email) knownData.email = parsed.email;
      if (parsed.phone) knownData.phone = parsed.phone;
      if (parsed.company) knownData.company = parsed.company;
      if (parsed.title) knownData.title = parsed.title;
      const { data: enrichData } = await apiPost<{
        confidence: string;
        fields: Record<string, any>;
        rawSummary: string;
      }>('/contacts/enrich', { name: searchName, hints: hints || undefined, knownData: Object.keys(knownData).length > 0 ? knownData : undefined });

      setEnrichResult(enrichData);
      if (enrichData.rawSummary) setEnrichmentSummary(enrichData.rawSummary);
      if (enrichData.fields?.skills?.length) setEnrichmentSkills(enrichData.fields.skills);
      if (enrichData.confidence) setEnrichmentConfidence(enrichData.confidence);

      // Auto-fill empty fields from enrichment
      if (enrichData.fields) {
        if (!parsed.company && enrichData.fields.company) { setCompany(enrichData.fields.company); setFirm(enrichData.fields.company); }
        if (!parsed.title && enrichData.fields.contactTitle) setContactTitle(enrichData.fields.contactTitle);
        if (!parsed.location && enrichData.fields.location) setLocation(enrichData.fields.location);
        if (enrichData.fields.linkedinUrl) setLinkedinUrl(enrichData.fields.linkedinUrl);
        if (enrichData.fields.twitterHandle) setXHandle(enrichData.fields.twitterHandle.replace(/^@/, ''));
        if (!parsed.phone && enrichData.fields.phone) setPhone(enrichData.fields.phone);
        if (enrichData.fields.bio && !summary) setSummary(enrichData.fields.bio);
      }
      setEnrichLoading(false);
      setFreeformParsing(false);
    } else {
      setFreeformParsing(false);
    }
  } catch (err: any) {
    console.error('[Freeform] Parse/enrich failed:', err);
    setFreeformParsing(false);
    setEnrichLoading(false);
  }
}
